import { useCallback } from "react";
import * as Sentry from "@sentry/react";

import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/posthog";
import { fileExtProp } from "@/lib/analytics/eventProps";
import { logger } from "@/utils/logger";

/**
 * Downloads a document from Storage as a blob and saves it through a
 * same-origin anchor click — the same mechanism the documents pages use.
 * Deliberately not `window.open(signedUrl)`: by the time the signed URL
 * resolves the click's transient user activation is gone, and Safari/Firefox
 * silently block the popup without throwing. Surfaces a toast on failure.
 */
export function useFileDownload(): (filePath: string | null, filename: string) => Promise<void> {
  const { t } = useLanguage();
  const { toast } = useToast();

  return useCallback(
    async (filePath: string | null, filename: string) => {
      if (!filePath) return;
      try {
        const { data, error } = await supabase.storage.from("documents").download(filePath);
        if (error) throw error;
        const blobUrl = URL.createObjectURL(data);
        const link = window.document.createElement("a");
        link.href = blobUrl;
        // Storage paths are `<org>/<building>/<uuid>.<ext>`, so the last path
        // segment is a UUID — the caller must supply the real filename.
        link.download = filename || (filePath.split("/").pop() ?? "download");
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        // Deferred like FileOutputChip: a same-tick revoke can abort the save
        // in Firefox/Safari (the anchor's href may still be resolving) without
        // ever throwing, so the catch/toast below would never fire.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        track("document_downloaded", { file_ext: fileExtProp(link.download), surface: "signing" });
      } catch (cause) {
        // Reported like the bulk download, under its own fingerprint so
        // single-file failures are not folded into the bulk ones in triage.
        const error = cause instanceof Error ? cause : new Error("Signing file download failed");
        const sentryEventId = Sentry.captureException(error, {
          tags: { feature: "signing", action: "file-download" },
          fingerprint: ["signing", "file-download"],
        });
        void logger.error("ui", "signing.file-download", "Signing file download failed", error, {
          sentry_event_id: sentryEventId,
        });
        toast({ title: t.signing.panel.downloadFailed, variant: "destructive" });
      }
    },
    [toast, t]
  );
}
